import React, { useCallback, useState } from "react";
import {
  Box,
  Checkbox,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  alpha,
} from "@mui/material";

const ANALYTICS_TABLE_SORT_STORAGE_PREFIX = "xpro.platform.analyticsTableSort.";

function readTableSort(storageKey, sortFields, defaultSort) {
  if (typeof sessionStorage === "undefined") {
    return { ...defaultSort };
  }
  try {
    const raw = sessionStorage.getItem(`${ANALYTICS_TABLE_SORT_STORAGE_PREFIX}${storageKey}`);
    if (!raw) return { ...defaultSort };
    const parsed = JSON.parse(raw);
    const sortBy = sortFields[parsed?.sortBy] ? String(parsed.sortBy) : defaultSort.sortBy;
    const sortOrder = parsed?.sortOrder === "desc" ? "desc" : "asc";
    return { sortBy, sortOrder };
  } catch {
    return { ...defaultSort };
  }
}

function writeTableSort(storageKey, sort) {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.setItem(`${ANALYTICS_TABLE_SORT_STORAGE_PREFIX}${storageKey}`, JSON.stringify(sort));
  } catch {
    /* quota / private mode */
  }
}

function compareAnalyticsRows(a, b, sortBy, sortOrder, sortFields) {
  const fieldKeys = Object.keys(sortFields);
  const cfg = sortFields[sortBy] || sortFields[fieldKeys[0]];
  const va = cfg.get(a);
  const vb = cfg.get(b);
  let cmp = 0;

  if (cfg.type === "number") {
    cmp = (Number(va) || 0) - (Number(vb) || 0);
  } else {
    cmp = String(va || "").localeCompare(String(vb || ""), undefined, { sensitivity: "base" });
  }

  return sortOrder === "desc" ? -cmp : cmp;
}

function SortableTableHeadCell({ columnId, align, sortBy, sortOrder, onSort, children }) {
  const active = sortBy === columnId;
  return (
    <TableCell align={align} sortDirection={active ? sortOrder : false}>
      <TableSortLabel
        active={active}
        direction={active ? sortOrder : "asc"}
        onClick={(e) => {
          e.stopPropagation();
          onSort(columnId);
        }}
      >
        {children}
      </TableSortLabel>
    </TableCell>
  );
}

/** Persiste ordenação por tabela (sessionStorage) — mesmo padrão de Uso do produto. */
export function useAnalyticsTableSort(storageKey, sortFields, defaultSort) {
  const [sort, setSort] = useState(() => readTableSort(storageKey, sortFields, defaultSort));

  const handleSort = useCallback(
    (columnId) => {
      if (!sortFields[columnId]) return;
      const nextOrder = sort.sortBy === columnId ? (sort.sortOrder === "asc" ? "desc" : "asc") : "asc";
      const next = { sortBy: columnId, sortOrder: nextOrder };
      setSort(next);
      writeTableSort(storageKey, next);
    },
    [sort.sortBy, sort.sortOrder, sortFields, storageKey]
  );

  const sortRows = useCallback(
    (rows) => [...rows].sort((a, b) => compareAnalyticsRows(a, b, sort.sortBy, sort.sortOrder, sortFields)),
    [sort.sortBy, sort.sortOrder, sortFields]
  );

  return { sortBy: sort.sortBy, sortOrder: sort.sortOrder, handleSort, sortRows };
}

export function AnalyticsKpiCard({
  title,
  value,
  periodLabel,
  accentColor,
  icon: Icon,
  details = [],
  onClick,
  selected = false,
  action,
}) {
  const detailLines = (Array.isArray(details) ? details : [details]).filter(Boolean);
  const clickable = typeof onClick === "function";
  return (
    <Paper
      variant="outlined"
      onClick={clickable ? onClick : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick(e);
              }
            }
          : undefined
      }
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      aria-pressed={clickable ? selected : undefined}
      sx={{
        p: { xs: 1.75, sm: 2 },
        height: "100%",
        minHeight: detailLines.length ? 132 : 118,
        display: "flex",
        flexDirection: "column",
        borderRadius: 2,
        borderColor: selected ? accentColor : "divider",
        borderLeft: "3px solid",
        borderLeftColor: accentColor,
        bgcolor: (theme) => alpha(accentColor, theme.palette.mode === "dark" ? (selected ? 0.22 : 0.14) : selected ? 0.14 : 0.07),
        cursor: clickable ? "pointer" : "default",
        boxShadow: selected ? 2 : 0,
        transition: "box-shadow 0.15s ease, transform 0.15s ease",
        ...(clickable
          ? {
              "&:hover": {
                boxShadow: 2,
                transform: "translateY(-1px)",
              },
              "&:focus-visible": {
                outline: "2px solid",
                outlineColor: accentColor,
                outlineOffset: 2,
              },
            }
          : {}),
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75, mb: 1 }}>
        {Icon ? (
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: (theme) => alpha(accentColor, theme.palette.mode === "dark" ? 0.28 : 0.14),
              color: accentColor,
              flexShrink: 0,
            }}
          >
            <Icon sx={{ fontSize: 18 }} aria-hidden />
          </Box>
        ) : null}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            flex: 1,
            minWidth: 0,
            fontWeight: 600,
            letterSpacing: 0.05,
            textTransform: "uppercase",
            fontSize: "0.68rem",
            lineHeight: 1.3,
            pt: 0.5,
            pr: action ? 0.5 : 0,
          }}
        >
          {title}
        </Typography>
        {action ? (
          <Box sx={{ flexShrink: 0, alignSelf: "flex-start" }} onClick={(e) => e.stopPropagation()}>
            {action}
          </Box>
        ) : null}
      </Box>
      <Typography variant="h5" sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums", lineHeight: 1.2 }}>
        {value}
      </Typography>
      {detailLines.length ? (
        <Box sx={{ mt: 0.75, display: "flex", flexDirection: "column", gap: 0.25 }}>
          {detailLines.map((line, idx) => (
            <Typography
              key={`${idx}-${line}`}
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: "0.68rem", fontVariantNumeric: "tabular-nums", lineHeight: 1.35 }}
            >
              {line}
            </Typography>
          ))}
        </Box>
      ) : null}
      {periodLabel ? (
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ mt: "auto", pt: 0.75, fontSize: "0.68rem" }}
        >
          {periodLabel}
        </Typography>
      ) : null}
    </Paper>
  );
}

export function AnalyticsSection({ title, hint, loading, children, sx }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3, ...sx }}>
      <Typography variant="subtitle2" sx={{ mb: hint ? 0.5 : 1 }}>
        {title}
      </Typography>
      {hint ? (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
          {hint}
        </Typography>
      ) : null}
      {loading ? <LinearProgress sx={{ mb: 1.5 }} /> : null}
      {children}
    </Paper>
  );
}

export function AnalyticsDataTable({
  rows,
  columns,
  emptyLabel,
  onRowClick,
  sortBy,
  sortOrder,
  onSort,
  sortFields,
  getRowSx,
  tableMinWidth = 720,
  selectable = false,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  getRowId = (row) => row.id,
}) {
  if (!rows?.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
        {emptyLabel}
      </Typography>
    );
  }

  const selected = selectedIds instanceof Set ? selectedIds : null;
  const selectedCount = selected ? selected.size : 0;
  const allSelected = selectable && selected && rows.length > 0 && selectedCount >= rows.length;
  const someSelected = selectable && selected && selectedCount > 0 && selectedCount < rows.length;

  return (
    <TableContainer sx={{ overflowX: "auto", maxWidth: "100%" }}>
      <Table size="small" sx={{ minWidth: tableMinWidth }}>
        <TableHead>
          <TableRow>
            {selectable ? (
              <TableCell padding="checkbox" sx={{ width: 42 }}>
                <Checkbox
                  size="small"
                  indeterminate={someSelected}
                  checked={allSelected}
                  onChange={onToggleSelectAll}
                  disabled={!onToggleSelectAll || rows.length === 0}
                  inputProps={{ "aria-label": "Selecionar todos" }}
                />
              </TableCell>
            ) : null}
            {columns.map((col) =>
              sortFields?.[col.id] && onSort ? (
                <SortableTableHeadCell
                  key={col.id}
                  columnId={col.id}
                  align={col.align || "left"}
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onSort={onSort}
                >
                  {col.label}
                </SortableTableHeadCell>
              ) : (
                <TableCell key={col.id} align={col.align || "left"} sx={col.headSx}>
                  {col.label}
                </TableCell>
              )
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            const rowId = getRowId(row);
            const isSelected = Boolean(selected?.has(rowId));
            return (
              <TableRow
                key={row._rowKey ?? rowId}
                hover={Boolean(onRowClick) || Boolean(getRowSx) || selectable}
                selected={isSelected}
                sx={[
                  getRowSx?.(row),
                  onRowClick ? { cursor: "pointer" } : null,
                ]}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
              >
                {selectable ? (
                  <TableCell
                    padding="checkbox"
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    <Checkbox
                      size="small"
                      checked={isSelected}
                      onChange={() => onToggleSelect?.(rowId)}
                      disabled={!onToggleSelect}
                      inputProps={{ "aria-label": `Selecionar ${rowId}` }}
                    />
                  </TableCell>
                ) : null}
                {columns.map((col) => (
                  <TableCell key={col.id} align={col.align || "left"} sx={col.cellSx}>
                    {col.render(row)}
                  </TableCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export function AnalyticsKpiGrid({ children, columns = 4 }) {
  const cols = Math.min(6, Math.max(2, Number(columns) || 4));
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          sm: "1fr 1fr",
          md: cols >= 5 ? "repeat(3, 1fr)" : `repeat(${Math.min(cols, 4)}, 1fr)`,
          lg: `repeat(${cols}, 1fr)`,
        },
        gap: 2,
      }}
    >
      {children}
    </Box>
  );
}
